import * as DOMElements from "../settings/DOMElements";
import * as Config from "../settings/config";
import * as _Int from "../settings/interfaces";

import * as contentLoader from "./content/contentLoader";
import * as draggable from "./draggable";
import * as clickable from './clickable';
import * as log from "./log";

// import * as instruction from './instructions'; // not sure if we need this

import * as custom from "../client/js/custom";

let currentCardDOM: HTMLDivElement;
export let topCardDOM: HTMLDivElement;
export let bottomCardDOM: HTMLDivElement;
export let leftCardDOM: HTMLDivElement;
export let rightCardDOM: HTMLDivElement;

export const drag = { now: false };


/** Initialises globals, loads the first set of cards into the container, and sets up the initial event handlers. */
export function init(): void {

  // set up global variables
  // needs to be done as init is called AFTER the DOM has loaded
  setGlobals();

  // get the first card details from the JSON
  const initCard: _Int.Path.ICard = Config.cards[0];

  // load content into the first card
  loadContent(initCard, currentCardDOM);

  // preload the next cards
  loadCards(initCard, true);

  // call custom init function
  if (Config.options.events && Config.options.events.init) {
    (custom as any)[Config.options.events.init]();
  }

}

/** Assigns global variables. */
function setGlobals(): void {

  currentCardDOM = document.querySelector(`#${DOMElements.currentCardID}`) as HTMLDivElement;

  leftCardDOM = document.querySelector(`#${DOMElements.leftCardID}`) as HTMLDivElement;
  rightCardDOM = document.querySelector(`#${DOMElements.rightCardID}`) as HTMLDivElement;
  topCardDOM = document.querySelector(`#${DOMElements.upCardID}`) as HTMLDivElement;
  bottomCardDOM = document.querySelector(`#${DOMElements.downCardID}`) as HTMLDivElement;

}

/**
 * Loads card content into a given DOM element.
 * @param card Card details (interface ICard) to load content.
 * @param cardDOM DOM element to load the content into.
 */
export function loadContent(card: _Int.Path.ICard, cardDOM: HTMLDivElement): void {

  // set id on DOM element
  cardDOM.dataset.cardId = card.id;

  // checks the current card for custom content
  const isCustom: boolean = card.content.some((c) => c.type === "custom_template");

  if (isCustom) {

    // load custom content into the DOM
    contentLoader.loadCustom(card, cardDOM);

  } else {

    // load predefined content into the DOM
    contentLoader.loadPreset(card, cardDOM);

  }

}

/**
 * Performs tasks after preloading card content, and handles first and last card cases.
 * @param card Current card data.
 * @param first True if this is the first card.
 */
function loadCards(card: _Int.Path.ICard, first: boolean = false) {

  // [explicit] if the current card has following cards
  if (preloadCards(card)) {
    // if it's not the last card (there are cards that have been preloaded)

    // if the card is a decision card
    const decisionGroupDOM = currentCardDOM.querySelector("#decision-group");
    if (decisionGroupDOM) { decisionGroupDOM.classList.add("show"); }

  } else { // last card

    // end the swiper
    swiperEnd();

  }

  // if the current card is the very first card
  if (first) {

    // begin the intruction animation
    // instruction.beginAnimate(currentCardDOM, 1.5, () => {

      // apply touch events once the animation is over
      draggable.addDraggable(currentCardDOM, card, (dir: string) => {
        nextCard(dir);
      });

      clickable.addClickable(currentCardDOM, (dir: string) => {
        nextCard(dir);
      });

    // });

  } else { // every other card

    // apply touch events
    draggable.addDraggable(currentCardDOM, card, (dir: string) => {
      nextCard(dir);
    });

    clickable.addClickable(currentCardDOM, (dir: string) => {
      nextCard(dir);
    });

  }

}

/**
 * Preloads next cards into the DOM.
 * @param card Current card data.
 */
function preloadCards(card: _Int.Path.ICard): boolean {

  // get the next cards' data
  const leftCard = Config.getCard(card.leftCard as string);
  const rightCard = Config.getCard(card.rightCard as string);
  const topCard = Config.getCard(card.topCard as string);
  const bottomCard = Config.getCard(card.bottomCard as string);

  // this array is to allow for looping though each card to load the content in
  const cards: Array<{ cardData: _Int.Path.ICard, DOM: HTMLDivElement }> = [];

  // if the current card has next card data, push it to the cards array,
  // otherwise clear the DOM
  if (leftCard) { cards.push({ cardData: leftCard, DOM: leftCardDOM }); }
  else { clearCardDOM(leftCardDOM); }

  if (rightCard) { cards.push({ cardData: rightCard, DOM: rightCardDOM }); }
  else { clearCardDOM(rightCardDOM); }

  if (topCard) { cards.push({ cardData: topCard, DOM: topCardDOM }); }
  else { clearCardDOM(topCardDOM); }

  if (bottomCard) { cards.push({ cardData: bottomCard, DOM: bottomCardDOM }); }
  else { clearCardDOM(bottomCardDOM); }

  // if there are no card references at all, assume that this is the last card
  if (!leftCard && !rightCard && !topCard && !bottomCard) {

    return false;

  } else { // otherwise, load in the content for each card

    for (const cardItem of cards) {
      loadContent(cardItem.cardData, cardItem.DOM);
    }

    return true;

  }

  /**
   * Perform necessary tasks for clearing a DOM element.
   * @param cardDOM The card DOM element to clear.
   */
  function clearCardDOM(cardDOM: HTMLDivElement): void {
    cardDOM.innerHTML = "";
    cardDOM.removeAttribute("data-card-id");
  }

}

/**
 * Transitions the next card in and performs tasks once the transition has ended.
 * @param direction Direction of the swipe.
 */
function nextCard(direction: string): void {

  // get the current card data from an attribute set on the current card DOM element
  let currentCardInfo = Config.getCard(currentCardDOM.dataset.cardId as string);

  // add an entry to the log
  log.addEntry(currentCardInfo as _Int.Path.ICard, direction);

  // set variables
  let DOM: HTMLDivElement = currentCardDOM; // had to assign to avoid errors
  let domID: string = ""; // had to assign to avoid errors
  let tagElement: string;

  // depending on the direction, assign variables above and transform the current card DOM to the swiped direction
  switch (direction) {
    case "left":
      DOM = rightCardDOM;
      domID = DOMElements.rightCardID;
      currentCardDOM.style.transform = `translate(-250%, 0px) rotate(-70deg)`;
      break;
    case "right":
      DOM = leftCardDOM;
      domID = DOMElements.leftCardID;
      currentCardDOM.style.transform = `translate(250%, 0px) rotate(70deg)`;
      break;
    case "top":
      DOM = bottomCardDOM;
      domID = DOMElements.downCardID;
      currentCardDOM.style.transform = `translateY(-250%)`;
      break;
    case "bottom":
      DOM = topCardDOM;
      domID = DOMElements.upCardID;
      currentCardDOM.style.transform = `translateY(250%)`;
      break;
  }

  tagElement = "#" + domID;

  // apply transitioning for the next card to become the current card, and transform it to bring it to the display
  DOM.style.transition = `transform .2s ease-out`;
  DOM.style.transform = `translate(0, 0) rotate(0deg)`;

  // add an event listener for once the transition is over and the next card has landed
  DOM.addEventListener("transitionend", cardLanded);

  /**
   * Performs resetting tasks for when the swipe transition ends.
   */
  function cardLanded(): void {

    // remove the end transition event listener from the next card DOM element as at the point it will become the current card
    DOM.removeEventListener("transitionend", cardLanded);

    // change the next card to the current card
    DOM.id = DOMElements.currentCardID;
    // change the now old current card to the next card
    currentCardDOM.id = domID;

    // reassign DOM to target the now old current card
    DOM = document.querySelector(tagElement) as HTMLDivElement;
    // reassign currentCardDOM to the new current card
    currentCardDOM = document.querySelector("#" + DOMElements.currentCardID) as HTMLDivElement;

    // remove the transition from the old current card to instantly position it
    DOM.style.transition = `none`;

    // instantly position the old current card to where the new current card used to be, effectively switching places
    switch (direction) {
      case "left":
        DOM.style.transform = `translateX(250%) rotate(70deg)`;
        rightCardDOM = DOM;
        break;
      case "right":
        DOM.style.transform = `translateX(-250%) rotate(-70deg)`;
        leftCardDOM = DOM;
        break;
      case "top":
        DOM.style.transform = `translateY(250%)`;
        bottomCardDOM = DOM;
        break;
      case "bottom":
        DOM.style.transform = `translateY(-250%)`;
        topCardDOM = DOM;
        break;
    }

    // reassign the currentCardInfo to that of the new card
    currentCardInfo = Config.getCard(currentCardDOM.dataset.cardId as string) as _Int.Path.ICard;

    // remove touch events from the old current card
    draggable.stopDraggable();

    // load a custom action as defined in the json & custom script
    if (currentCardInfo.event) {
      const [fnName, ...params] = currentCardInfo.event;
      (custom as any)[fnName](currentCardInfo, params);
    }

    // load in the next cards
    loadCards(currentCardInfo);

  }

}

function swiperEnd(): void {

  // console.log("END");

}